import React, { FC } from "react";
import * as Styled from "./Exposure.css";
import ScrollArea from "src/components/ScrollArea";
import HugeIcon from "src/components/HugeIcon";
import {
  InformationCircleIcon,
  SlidersHorizontalIcon,
} from "@hugeicons/core-free-icons";
import { CardNote, EmptyState } from "src/components/ui";
import { useBox, useRoomFor } from "src/hooks/useCardRoom";

export interface ExposureBand {
  name: string;
  min: number;
  max: number;
}

interface IExposure {
  data: ExposureBand[];
  /**
   * How many individual exposures sit under the bands.
   *
   * Read by `exposureTooltip`, not by the body — see the note there.
   */
  totalIExposures?: number;
}

/**
 * The caveat and the count, in the card's tooltip.
 *
 * Both used to be printed under the bands: two lines of small grey prose and
 * a bordered row carrying one figure, together about ninety pixels — a third
 * of the card's body — spent on material a reader consults ONCE. Meanwhile
 * the bands themselves, which is what the card is for and what changes from
 * smartfolio to smartfolio, were scrolling inside what was left.
 *
 * Neither is a reading of the data. "Indicative" is a standing property of
 * every band on the card, and the count of underlying exposures is
 * provenance — how the four bands were arrived at. Provenance belongs where
 * the card explains itself, which this card already has: the info mark beside
 * its name. So they move there and the bands get the room back.
 */
export const exposureTooltip = (count?: number) => ({
  title: (
    <>
      <div>
        Bounds, not weights. Each exposure is rebalanced somewhere inside its
        band over time, so the bands do not add up to a whole.
      </div>
      {count ? (
        <div className="fina-tip-stat">
          <span>Individual exposures</span>
          <b>{count.toLocaleString("en-US")}</b>
        </div>
      ) : null}
    </>
  ),
  icon: <HugeIcon icon={InformationCircleIcon} size={14} />,
});

/**
 * The bands a smartfolio's main exposures move within.
 *
 * Not weights — BOUNDS. "India Stocks 35 – 80%" says the holding is
 * rebalanced somewhere inside that band as the risk profile moves, so these
 * do not add to a whole and have no place in a ring.
 *
 * They were tinted tiles, then a two-column table, and both were the same
 * mistake in different clothes: a range printed as two numbers is read as
 * two numbers. What a reader wants from a set of ranges is which exposure
 * dominates and how much latitude each has, and that is a question about
 * WIDTH and POSITION — so each one is drawn on a common 0-100 track, with
 * its band where it sits. Four bands on one scale answer at a glance what
 * four pairs of digits made you work out: India can run to eighty per cent
 * of the holding while the other three are pinned inside a fifteen-point
 * window near the bottom.
 *
 * The digits stay, at the end of the row, because a band says roughly and
 * some readers want exactly.
 *
 * THE LIST SCROLLS ONLY WHEN IT HAS TO. A smartfolio with eleven main
 * exposures would otherwise make this card twice the height of the ring
 * cards beside it, so the pane is capped — but capped rather than fixed,
 * which is the difference between a card of five bands ending under its
 * fifth and a card of five bands padded out to eleven's height and still
 * flying a scroll cue. The scale stays OUTSIDE the scroll, because a scale
 * a reader has to scroll to reach is not an axis.
 */
export const Exposure: FC<IExposure> = ({ data }) => {
  /**
   * Room left over, and the one thing worth saying in it.
   *
   * The bands and their scale are the whole of this card, so what the row
   * hands it beyond their own height is paper. The sum is the bands' own
   * geometry (see the constants beside BANDS_CAP), not a measurement of the
   * rendered rows, so the note cannot chase its own tail.
   *
   * What goes there is the sentence that governs how every figure on the
   * card is read, and it was behind the info mark: these are BOUNDS. A
   * reader who takes "10 to 30%" for a weight has misread the card, and
   * that is not something to hide behind a hover.
   *
   * The box measured is the WRAPPER, which does not contain the note. Ask
   * the frame and the note is measuring the room it is itself taking up:
   * on a card sized by its own content it would find that room every time
   * and never go away again.
   */
  const [holdWrapper, box] = useBox();
  const need = Styled.bandsHeight(data?.length ?? 0);
  const roomy = useRoomFor(box.height - need);

  /**
   * No bands: SAY so, do not draw the scale anyway.
   *
   * The card rendered its 0-50-100 axis whatever it was given, so a
   * smartfolio whose factsheet carries no exposures — and every smartfolio
   * for the second or two the factsheet is in flight — showed a ruler with
   * nothing measured against it under a card that promised its main
   * exposures. An axis is a way of reading marks; on its own it is a
   * mislabel, and it read as a chart that had failed to draw.
   *
   * The scale belongs to the bands, so it goes when they do, and the card
   * uses the product's own empty state instead. It says which of the two
   * cases this is, because the reader can tell them apart from the answer
   * they get: a factsheet that does not publish bands is not the same as a
   * card that is broken.
   */
  if (!data?.length) {
    return (
      <Styled.Frame>
        <EmptyState
          icon={SlidersHorizontalIcon}
          title="No main exposures published"
          hint="This Smartfolio's factsheet does not state the bands its exposures move within."
        />
      </Styled.Frame>
    );
  }

  return (
    <Styled.Frame>
      <Styled.Wrapper ref={holdWrapper}>
        {/* A ceiling, not a height: the pane is as tall as the bands until
        there are more than the cap holds. Given a fixed height it padded a
        short list out and flew its scroll cue over a list that was already
        complete. */}
        <ScrollArea maxHeight={Styled.BANDS_CAP}>
          <Styled.Bands>
            {data.map(({ name, min, max }) => {
              const from = Math.max(0, Math.min(100, min));
              const to = Math.max(from, Math.min(100, max));
              return (
                <Styled.Band key={name}>
                  <Styled.BandHead>
                    <Styled.Name>{name}</Styled.Name>
                    <Styled.Bounds>{`${min} – ${max}%`}</Styled.Bounds>
                  </Styled.BandHead>
                  {/* One scale for every row, so the bands are comparable. A track
                that started at each row's own minimum would make every band
                look the same width. */}
                  <Styled.Track>
                    <Styled.Fill
                      style={{ left: `${from}%`, width: `${to - from}%` }}
                    />
                  </Styled.Track>
                </Styled.Band>
              );
            })}
          </Styled.Bands>
        </ScrollArea>

        <Styled.Scale>
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </Styled.Scale>
      </Styled.Wrapper>
      {roomy ? (
        <CardNote>
          <span>
            These are ranges, not fixed weights. Each exposure moves inside its
            own band as the Smartfolio is rebalanced.
          </span>
        </CardNote>
      ) : null}
    </Styled.Frame>
  );
};
